/**
 * Rename, delete, and create country-scoped preset folders
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Check, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui/form';
import { backdropClick } from '@/components/ui';

export interface PresetFoldersManageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: Array<{ id: string; label: string }>;
  isLoading: boolean;
  isBusy: boolean;
  onRename: (id: string, label: string) => void | Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
  onCreate?: (label: string) => void | Promise<void>;
  isCreateBusy?: boolean;
}

function FolderRow(props: {
  folder: { id: string; label: string };
  disabled: boolean;
  onSave: (id: string, label: string) => void;
  onDelete: (id: string) => void;
}) {
  const { folder, disabled, onSave, onDelete } = props;
  const [val, setVal] = useState(folder.label);
  useEffect(() => setVal(folder.label), [folder.id, folder.label]);
  const trimmed = val.trim();
  const dirty = trimmed.length > 0 && trimmed !== folder.label.trim();
  return (
    <li className="flex items-center gap-2 rounded-lg border border-border/35 bg-muted/10 px-2 py-1.5 transition-colors hover:border-border/55">
      <Input
        className="h-9 flex-1 border-0 bg-transparent px-2 shadow-none focus-visible:ring-1 focus-visible:ring-ring"
        value={val}
        maxLength={80}
        disabled={disabled}
        onChange={(e) => setVal(e.target.value)}
        aria-label={`Rename folder ${folder.label}`}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        title="Save name"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
        disabled={!dirty || disabled}
        aria-label="Save folder name"
        onClick={() => onSave(folder.id, trimmed)}
      >
        <Check className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        title="Remove from all presets"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
        disabled={disabled}
        aria-label="Delete folder"
        onClick={() => onDelete(folder.id)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}

export const PresetFoldersManageDialog: FC<PresetFoldersManageDialogProps> = ({
  open,
  onOpenChange,
  folders,
  isLoading,
  isBusy,
  onRename,
  onDelete,
  onCreate,
  isCreateBusy = false,
}) => {
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    if (!open) setNewLabel('');
  }, [open]);

  if (!open) return null;

  const confirmDelete = (id: string) => {
    if (!confirm('Delete this folder? It will be removed from every preset that uses it.')) return;
    void onDelete(id);
  };

  const handleCreate = () => {
    const t = newLabel.trim();
    if (!t || !onCreate) return;
    void onCreate(t);
    setNewLabel('');
  };

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-background/70 p-4 backdrop-blur-md"
      onClick={backdropClick(() => onOpenChange(false))}
      role="presentation"
    >
      <div
        className="flex max-h-[min(85vh,32rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preset-folders-manage-title"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border/50 px-5 pb-4 pt-5">
          <div className="min-w-0 space-y-1">
            <h2 id="preset-folders-manage-title" className="text-base font-semibold tracking-tight">
              Preset folders
            </h2>
            <p className="text-xs text-muted-foreground">
              Filter labels for this country; updates apply on every preset that uses them.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 rounded-full"
            aria-label="Close"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 scrollbar-thin">
          {onCreate ? (
            <div className="mb-4 flex flex-col gap-2 rounded-lg border border-dashed border-border/60 bg-muted/10 p-3">
              <span className="text-xs font-medium text-muted-foreground">New folder</span>
              <div className="flex gap-2">
                <Input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Folder name"
                  maxLength={80}
                  className="h-9 flex-1"
                  disabled={isBusy || isCreateBusy}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  aria-label="New folder name"
                />
                <Button
                  type="button"
                  size="sm"
                  className="h-9 shrink-0"
                  disabled={isBusy || isCreateBusy || !newLabel.trim()}
                  onClick={handleCreate}
                >
                  Create
                </Button>
              </div>
            </div>
          ) : null}
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : folders.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border/60 bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
              No folders yet. Create one above or when editing a preset.
            </p>
          ) : (
            <ul className="flex flex-col gap-2" role="list">
              {folders.map((t) => (
                <FolderRow
                  key={t.id}
                  folder={t}
                  disabled={isBusy}
                  onSave={onRename}
                  onDelete={confirmDelete}
                />
              ))}
            </ul>
          )}
        </div>
        <footer className="border-t border-border/50 px-5 py-3">
          <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </footer>
      </div>
    </div>
  );
};
